import React, { useRef } from 'react';
import classNames from "./RecordingPanelSideBarView.module.css"
import { observer } from 'mobx-react-lite';
import { RecordingPanelState } from './RecordingPanelState';
import { CallStackResolutionSelectorView } from '../../lib/Causation/CallStackResolutionSelectorView';
import { CallStackResolution } from '../../lib/Causation/CallStackResolution';
import { GlobalContext } from './GlobalContext';
import { isRunningInExtensionsDevToolsPanel, isRunningInExtensionsSidePanel } from '../../Pages/devtools/isProcessRunningInExtensionPage';
import { ServiceWorkerAPIClient } from '../../lib/API/ServiceWorkerApiClient';
import { DevToolsPanelState } from '../../Pages/DevToolsPanelState';



export const RecordingPanelSideBarView = observer(({
	recordingPanelState,
	devToolsPanelState,
}: { recordingPanelState: RecordingPanelState; devToolsPanelState?: DevToolsPanelState; }) => {
	const recordingsSearchTermsInputTextRef = useRef<HTMLTextAreaElement>(null);

	const globalContext = React.useContext(GlobalContext);
	if (globalContext == null) return;
	if (recordingPanelState.primaryTabId === 'Analytics') return null;
	const tabId = devToolsPanelState?.tabRecordingState?.tabId
	const {recordingStatus, searchUiState, dbRecordingEntryWriter, recordingUuid} = recordingPanelState;
	const isRecording = recordingStatus === 'recording';
	const {licenseCache} = globalContext;
	const newDomGeneratedSearchTermsToIgnore = (searchTerm: string) => ({
		domGeneratedSearchTermsToIgnore: [
			...(dbRecordingEntryWriter?.recording?.domGeneratedSearchTermsToIgnore ?? []),
			searchTerm
		]
	});
	const removeSuggestedSearchTerm = async (searchTerm: string) => {
		await dbRecordingEntryWriter?.updateRecording(newDomGeneratedSearchTermsToIgnore(searchTerm));
	};
	const ignoreSuggestedSearchTerm = (searchTerm: string) => () => removeSuggestedSearchTerm(searchTerm);
	const acceptSuggestedSearchTerm = (searchTerm: string) => async () => {
		const searchTermsNewlineSeparated = [...new Set<string>([
			...(dbRecordingEntryWriter?.recording?.searchTermsNewlineSeparated ?? "").split("\n")
				.map(s => s.trim())
				.filter(s => s.length > 0),
			searchTerm
		])].sort().join("\n");
		searchUiState.setSearchTermTextAreaValueWithoutUpdatingDatabase(searchTermsNewlineSeparated);
		await dbRecordingEntryWriter?.updateRecording({
			...newDomGeneratedSearchTermsToIgnore(searchTerm), searchTermsNewlineSeparated
		});
		removeSuggestedSearchTerm(searchTerm);
		// without this the text-area won't be updated because the value is changed only in textarea's defaultValue field
		if (recordingsSearchTermsInputTextRef.current != null) {
			recordingsSearchTermsInputTextRef.current.value = searchTermsNewlineSeparated;
		}
	};
	const setCallStackResolution = async (callStackResolution: CallStackResolution) => {
		await dbRecordingEntryWriter?.updateRecording({callStackResolution});
	}

	return (
	<div className={classNames.ControlPanelContainerElement}>
		{ isRunningInExtensionsDevToolsPanel || isRunningInExtensionsSidePanel ? (<>
				<div className={classNames.RecordingPanelSidebarHeaderElement}>
					{isRecording ? (<>recording in progress</>) : (<>recording stopped</>)}
				</div>{ isRecording && recordingUuid != null ? (<>
					<button onClick={ () => ServiceWorkerAPIClient.stopRecording({recordingUuid, stopScreenRecording: true}) }
						> 🟥 stop recording</button>
				</>) : recordingUuid != null && tabId != null ? (<>
					<button onClick={ () => ServiceWorkerAPIClient.resumeRecording({recordingUuid, tabId})} >resume recording</button>
				</>) : null }
			</>) : null
		}
		{ licenseCache.mayGenerateCausationGraph == false ? null : (<>
			<div className={classNames.RecordingPanelSidebarHeaderElement}>
				causation graphs
			</div>
				<div className={classNames.ControlWithLabelOnTopContainer}>
				<label>detail level</label>
				<CallStackResolutionSelectorView
					defaultValue={recordingPanelState.callStackResolution}
					set={setCallStackResolution}
				/>
				</div>
		</>)}
		<div className={classNames.RecordingPanelSidebarHeaderElement}>
			search terms
		</div>
		<div className={classNames.SearchControlsContentSectionElement}>
			<div className={classNames.ControlWithLabelOnTopContainer}>
				<label>Suggested</label>
				<div className={classNames.suggestedSearchTerms}>{recordingPanelState.searchUiState.searchTermsFromDomEvents.map(([searchTerm, searchByDefault]) => (
					<div key={searchTerm}>
						<button className={'action-remove'} onClick={ignoreSuggestedSearchTerm(searchTerm)}>✗</button>
						<button onClick={acceptSuggestedSearchTerm(searchTerm)}>✓</button>
						<span className={searchByDefault ? classNames.SearchTermAddedByDefault : classNames.SearchTermNotAddedByDefault}>{searchTerm}</span>
					</div>
				))}
				</div>
			</div>
			<div className={classNames.ControlWithLabelOnTopContainer}>
				<label>For this recording</label>
				<textarea
					ref={recordingsSearchTermsInputTextRef}
					defaultValue={recordingPanelState.searchUiState.searchTermTextAreaValue}
					onChange={(e) => recordingPanelState.searchUiState.setSearchTermTextAreaValue(e.currentTarget.value)} />
			</div>
			<div className={classNames.ControlWithLabelOnTopContainer}>
				<label>For all recordings</label>
				<textarea
					defaultValue={recordingPanelState.searchUiState.GlobalSearchTermTextAreaValue}
					onChange={(e) => { recordingPanelState.searchUiState.setGlobalSearchTermAreaValue(e.currentTarget.value); }} />
			</div>
		</div>
	</div>);
});
